/**
 * HubBreadcrumb — two-level stacked breadcrumb trail for constrained width.
 *
 * Renders breadcrumb as two stacked lines optimized for ~350px sidebar width:
 *
 * Examples:
 *   library-list   → [Workspace] (single line, current)
 *   list           → [Workspace] (line 1, muted)
 *                    [Library name] (line 2, current)
 *   detail         → [Workspace] › [Library] (line 1, muted)
 *                    [Document name] (line 2, current)
 *   creating       → [Workspace] (line 1, muted)
 *                    New library (line 2, current)
 *   uploading      → [Workspace] › [Library] (line 1, muted)
 *                    Uploading (line 2, current)
 *
 * Line 1: Context path (muted, 9px, secondary)
 * Line 2: Current segment (prominent, 11-12px, bold, primary)
 *
 * Zero business logic — derives segments purely from its props.
 */

import { makeStyles, mergeClasses } from "@fluentui/react-components";
import React from "react";
import { brandColors, fonts, inkColors } from "../tokens";
import type { HubMode } from "./LibraryHubSlideOver";

export interface HubBreadcrumbProps {
  mode: HubMode;
  workspaceName: string;
  libraryName?: string | null;
  docFilename?: string | null;
}

interface Segment {
  label: string;
  isCurrent: boolean;
}

interface BreadcrumbLines {
  line1: Segment[];
  line2: Segment[];
}

function buildBreadcrumbLines(
  mode: HubMode,
  workspaceName: string,
  libraryName: string | null | undefined,
  docFilename: string | null | undefined,
): BreadcrumbLines {
  const workspace: Segment = { label: workspaceName, isCurrent: false };

  switch (mode) {
    case "library-list":
    case "empty":
      // Single line: workspace as current
      return {
        line1: [],
        line2: [{ ...workspace, isCurrent: true }],
      };

    case "creating":
      // Context: workspace, Current: "New library"
      return {
        line1: [workspace],
        line2: [{ label: "New library", isCurrent: true }],
      };

    case "uploading":
      // Context: workspace › [library], Current: "Uploading"
      return {
        line1: [workspace, ...(libraryName ? [{ label: libraryName, isCurrent: false }] : [])],
        line2: [{ label: "Uploading", isCurrent: true }],
      };

    case "list":
      // Context: workspace, Current: library name
      return {
        line1: [workspace],
        line2: [{ label: libraryName ?? "Library", isCurrent: true }],
      };

    case "detail":
      // Context: workspace › library, Current: filename
      return {
        line1: [workspace, { label: libraryName ?? "Library", isCurrent: false }],
        line2: [{ label: docFilename ?? "Document", isCurrent: true }],
      };
  }
}

const useStyles = makeStyles({
  root: {
    display: "flex",
    flexDirection: "column",
    rowGap: "1px",
    minWidth: 0,
    flex: 1,
    overflow: "hidden",
  },
  line: {
    display: "flex",
    alignItems: "center",
    columnGap: "3px",
    minWidth: 0,
    overflow: "hidden",
  },
  // Line 1: context path (muted, smaller)
  line1: {
    fontSize: "9px",
    color: inkColors.ink4,
  },
  // Line 2: current level (prominent, bold, larger)
  line2: {
    fontSize: "11.5px",
    fontWeight: 600,
    color: inkColors.ink2,
  },
  segment: {
    fontFamily: fonts.mono,
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis",
    letterSpacing: "0.2px",
  },
  segmentLine1: {
    maxWidth: "200px",
  },
  segmentLine2: {
    maxWidth: "240px",
  },
  separator: {
    fontSize: "9px",
    color: inkColors.ink4,
    flexShrink: 0,
    userSelect: "none",
    // Lighten separator on hover for future clickable segments
    ":hover": {
      color: brandColors.primary,
    },
  },
});

export const HubBreadcrumb: React.FC<HubBreadcrumbProps> = ({ mode, workspaceName, libraryName, docFilename }) => {
  const styles = useStyles();
  const { line1, line2 } = buildBreadcrumbLines(mode, workspaceName, libraryName, docFilename);

  const renderSegmentLine = (segments: Segment[], lineClass: string) => {
    if (segments.length === 0) return null;

    return (
      <div className={mergeClasses(styles.line, lineClass)}>
        {segments.map((seg, idx) => (
          <React.Fragment key={seg.label}>
            {idx > 0 && (
              <span className={styles.separator} aria-hidden="true">
                ›
              </span>
            )}
            <span
              className={mergeClasses(
                styles.segment,
                lineClass === styles.line1 ? styles.segmentLine1 : styles.segmentLine2,
              )}
              aria-current={seg.isCurrent ? "page" : undefined}
              title={seg.label}
            >
              {seg.label}
            </span>
          </React.Fragment>
        ))}
      </div>
    );
  };

  return (
    <nav className={styles.root} aria-label="Library hub navigation path">
      {renderSegmentLine(line1, styles.line1)}
      {renderSegmentLine(line2, styles.line2)}
    </nav>
  );
};

export default HubBreadcrumb;
